import _extends from "@babel/runtime/helpers/extends";
import { Box, Text, TextButton, Tooltip } from '@wix/design-system';
import { InfoCircleSmall, StatusAlertFilledSmall, StatusCompleteFilledSmall, StatusWarningFilledSmall } from '@wix/wix-ui-icons-common';
import React, { useEffect, useMemo } from 'react';
import { getManualFixSurfaceProps } from '../../../../utils/get-rule-element-props-from-presentation';
import { ThemeAwareIcon } from '../../../../utils/theme-aware-icon';
import { usePreviousState } from '../../../../utils/use-previous-state';
import { resolveWdsBoxColorProps, wdsThemeColors } from '../../../../utils/wds-theme-colors';
import { ElementStatus } from '../../../rule-element/types';
import { useSharedAltTextScreenContext } from '../context/alt-text-screen-context';
import { getDisabledButtonTooltipKeySuffix, isAiSupportedImageFormat } from '../utils/get-disabled-button-tooltip-key';
export const getIsAiSupported = image => {
  if (image.isAiSupported !== undefined && image.altText) {
    return image.isAiSupported;
  }
  return isAiSupportedImageFormat(image.url);
};
export const sortUnsupportedFailedFirst = images => {
  const unsupportedFailed = images.filter(img => !getIsAiSupported(img) && img.status === ElementStatus.Failed);
  const rest = images.filter(img => getIsAiSupported(img) || img.status !== ElementStatus.Failed);
  return [...unsupportedFailed, ...rest];
};
const calculateSummaryCounts = images => {
  const generated = [];
  const failed = [];
  const unsupported = [];
  for (const img of images) {
    const isSupported = getIsAiSupported(img);
    if (!isSupported && img.status === ElementStatus.Failed) {
      unsupported.push(img);
    } else if (img.status === ElementStatus.GeneratedAll) {
      generated.push(img);
    } else if (img.status === ElementStatus.Warning) {
      failed.push(img);
    }
  }
  return {
    failed,
    generated,
    unsupported
  };
};
export const AltTextSummary = _ref => {
  let {
    images,
    isGenerating = false,
    onRetryFailed,
    onSortUnsupported,
    biCallbacks
  } = _ref;
  const {
    translations: ctx,
    wizardPresentation
  } = useSharedAltTextScreenContext();
  const translations = ctx.summary;
  const summarySurfaceProps = wizardPresentation ? getManualFixSurfaceProps(wizardPresentation) : {
    backgroundColor: 'B60'
  };
  const summaryBorderProps = wizardPresentation != null && wizardPresentation.softIssueListSurfaces ? resolveWdsBoxColorProps(wdsThemeColors.borderDarkSecondary, 'borderColor') : {
    borderColor: 'B40'
  };
  const {
    generated,
    failed,
    unsupported
  } = useMemo(() => calculateSummaryCounts(images), [images]);
  const generatedCount = generated.length;
  const failedCount = failed.length;
  const unsupportedCount = unsupported.length;
  const tooltipKeySuffix = getDisabledButtonTooltipKeySuffix(unsupported);
  const biPayload = useMemo(() => ({
    failed: failedCount,
    generated: generatedCount,
    unsupportedTotal: unsupportedCount
  }), [failedCount, generatedCount, unsupportedCount]);
  const prevGeneratedCount = usePreviousState(generatedCount);
  useEffect(() => {
    const isFirstRender = prevGeneratedCount === undefined;
    const hasCountChanged = generatedCount !== prevGeneratedCount;
    const shouldNotify = generatedCount > 0 && !isGenerating && (isFirstRender || hasCountChanged);
    if (shouldNotify) {
      biCallbacks.onSummaryView(biPayload);
    }
  }, [biCallbacks, biPayload, generatedCount, prevGeneratedCount, isGenerating]);
  const shouldShowAnything = generatedCount > 0 || failedCount > 0 || unsupportedCount > 0;
  if (!shouldShowAnything) {
    return null;
  }
  const handleRetryClick = () => {
    biCallbacks.onRetryClick(biPayload);
    onRetryFailed == null || onRetryFailed(failed);
  };
  const handleSortClick = () => {
    biCallbacks.onSortClick(biPayload);
    onSortUnsupported == null || onSortUnsupported(sortUnsupportedFailedFirst(images));
  };
  const handleTooltipShow = () => {
    if (tooltipKeySuffix) {
      biCallbacks.onTooltipView(tooltipKeySuffix);
    }
  };
  return /*#__PURE__*/React.createElement(Box, _extends({
    border: "1px solid",
    borderRadius: "4px" // TODO: consider replacing px with SP token
    ,
    boxSizing: "border-box",
    dataHook: "alt-text-summary",
    direction: "vertical",
    gap: "SP1",
    padding: "SP2",
    width: "100%"
  }, summarySurfaceProps, summaryBorderProps), generatedCount > 0 && /*#__PURE__*/React.createElement(Box, {
    verticalAlign: "middle",
    width: "100%"
  }, /*#__PURE__*/React.createElement(ThemeAwareIcon, {
    color: wdsThemeColors.statusSuccess,
    dataHook: "alt-text-summary-generated-icon",
    paddingRight: "SP1"
  }, /*#__PURE__*/React.createElement(StatusCompleteFilledSmall, null)), /*#__PURE__*/React.createElement(Text, {
    dataHook: "alt-text-summary-generated-text",
    size: "tiny",
    weight: "bold"
  }, translations.generatedText(generatedCount))), failedCount > 0 && /*#__PURE__*/React.createElement(Box, {
    verticalAlign: "middle"
  }, /*#__PURE__*/React.createElement(ThemeAwareIcon, {
    color: wdsThemeColors.statusWarning,
    dataHook: "alt-text-summary-failed-icon",
    paddingRight: "SP1"
  }, /*#__PURE__*/React.createElement(StatusWarningFilledSmall, null)), /*#__PURE__*/React.createElement(Text, {
    dataHook: "alt-text-summary-failed-text",
    size: "tiny",
    weight: "bold"
  }, translations.failedText(failedCount)), onRetryFailed && /*#__PURE__*/React.createElement(Box, {
    paddingLeft: "SP1"
  }, /*#__PURE__*/React.createElement(TextButton, {
    dataHook: "alt-text-summary-failed-button",
    size: "tiny",
    skin: "dark",
    underline: "always",
    onClick: handleRetryClick
  }, translations.tryAgainLabel))), unsupportedCount > 0 && /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP1"
  }, /*#__PURE__*/React.createElement(Box, {
    verticalAlign: "top"
  }, /*#__PURE__*/React.createElement(ThemeAwareIcon, {
    color: wdsThemeColors.statusDestructive,
    dataHook: "alt-text-summary-unsupported-icon",
    paddingRight: "SP1"
  }, /*#__PURE__*/React.createElement(StatusAlertFilledSmall, null)), /*#__PURE__*/React.createElement(Text, {
    dataHook: "alt-text-summary-unsupported-text",
    size: "tiny",
    weight: "bold"
  }, translations.unsupportedText(unsupportedCount), /*#__PURE__*/React.createElement(Tooltip, {
    content: translations.getUnsupportedTooltip(tooltipKeySuffix),
    dataHook: "alt-text-summary-unsupported-tooltip",
    maxWidth: 182,
    placement: "top",
    size: "small",
    inline: true,
    onShow: handleTooltipShow
  }, /*#__PURE__*/React.createElement(ThemeAwareIcon, {
    color: wdsThemeColors.iconSecondary,
    marginLeft: "SP1",
    style: {
      verticalAlign: 'text-bottom'
    },
    inline: true
  }, /*#__PURE__*/React.createElement(InfoCircleSmall, null))))), onSortUnsupported && /*#__PURE__*/React.createElement(Box, {
    paddingLeft: "SP5"
  }, /*#__PURE__*/React.createElement(TextButton, {
    dataHook: "alt-text-summary-unsupported-button",
    size: "tiny",
    skin: "dark",
    underline: "always",
    onClick: handleSortClick
  }, translations.sortFirstLabel))));
};
//# sourceMappingURL=alt-text-summary.js.map